import {
  type ProductBrowseRequest,
  ProductPublicationStatus,
  ProductStockStatus,
  type Currency,
  type TaxonomyTerm,
} from '@skin-seoul/service';
import { type FilterOptions, type FilterSummary } from '../types/filters';
import { findTermIdBySlug, type TaxonomyCategory } from './categoryUtils';
import { findBrandTermIdFromSummary, findBrandTermIdFromTaxonomy } from './brandUtils';

export interface RouteParams {
  category?: string;
  subcategory?: string;
  subSubcategory?: string;
  brand?: string;
  productTag?: string;
  collectionName?: string;
}

export const buildBrandsArray = (
  routeBrand?: string,
  filterBrands?: number[],
  hasFilterCategories?: boolean,
  filterSummary?: FilterSummary,
  brandTaxonomy?: TaxonomyTerm[],
): string => {
  if (filterBrands && filterBrands.length > 0) {
    return filterBrands.join(',');
  }

  if (hasFilterCategories) {
    return '';
  }

  if (routeBrand) {
    if (brandTaxonomy) {
      const brandTermId = findBrandTermIdFromTaxonomy(routeBrand, brandTaxonomy);
      if (brandTermId) {
        return brandTermId.toString();
      }
    }

    if (filterSummary) {
      const brandTermId = findBrandTermIdFromSummary(routeBrand, filterSummary);
      if (brandTermId) {
        return brandTermId.toString();
      }
    }

    return '';
  }

  return '';
};

export const buildCategoriesArray = (
  routeParams: RouteParams,
  filterCategories?: number[],
  categories?: TaxonomyCategory[],
): string => {
  const { category, subcategory, subSubcategory } = routeParams;
  if (filterCategories && filterCategories.length > 0) {
    return filterCategories.join(',');
  }

  if (categories) {
    // Priority order: sub-subcategory > subcategory > category
    if (subSubcategory) {
      const subSubcategoryTermId = findTermIdBySlug(categories, subSubcategory);
      if (subSubcategoryTermId) {
        return subSubcategoryTermId.toString();
      }
    } else if (subcategory) {
      const subcategoryTermId = findTermIdBySlug(categories, subcategory);
      if (subcategoryTermId) {
        return subcategoryTermId.toString();
      }
    } else if (category) {
      const categoryTermId = findTermIdBySlug(categories, category);
      if (categoryTermId) {
        return categoryTermId.toString();
      }
    }
  }

  if (subSubcategory || subcategory || category) {
    console.warn('Failed to find termId for category slug:', { category, subcategory, subSubcategory });
  }

  return '';
};

export interface PaginationParams {
  page: number;
  limit: number;
}

interface FilterableRequest {
  brands?: string;
  skinTypes?: string;
  skinConcerns?: string;
  ingredients?: string;
  categories?: string;
  minPrice?: number;
  maxPrice?: number;
}

export const addFilterParametersToRequest = (params: FilterableRequest, filters: FilterOptions): void => {
  if (filters.brands && filters.brands.length > 0) {
    params.brands = filters.brands.join(',');
  }
  if (filters.skinTypes && filters.skinTypes.length > 0) {
    params.skinTypes = filters.skinTypes.join(',');
  }
  if (filters.skinConcerns && filters.skinConcerns.length > 0) {
    params.skinConcerns = filters.skinConcerns.join(',');
  }
  if (filters.ingredients && filters.ingredients.length > 0) {
    params.ingredients = filters.ingredients.join(',');
  }
  if (filters.categories && filters.categories.length > 0) {
    params.categories = filters.categories.join(',');
  }

  if (filters.minPrice !== undefined) {
    params.minPrice = filters.minPrice;
  }
  if (filters.maxPrice !== undefined) {
    params.maxPrice = filters.maxPrice;
  }
};

export const buildProductBrowseParams = (
  routeParams: RouteParams,
  filters: FilterOptions,
  currency: Currency,
  pagination: PaginationParams,
  sortBy?: string,
  filterSummary?: FilterSummary,
  brandTaxonomy?: TaxonomyTerm[],
  transformedCategories?: TaxonomyCategory[],
): ProductBrowseRequest => {
  const params: ProductBrowseRequest = {
    page: pagination.page,
    limit: pagination.limit,

    publicationStatus: ProductPublicationStatus.publish,
    stockStatus: ProductStockStatus.instock,
    currency,

    sortBy: sortBy || 'BestSeller',
  };

  const hasFilterCategories = Boolean(filters.categories && filters.categories.length > 0);

  const categoriesStr = buildCategoriesArray(routeParams, filters.categories, transformedCategories);

  if (categoriesStr) {
    params.categories = categoriesStr;
  }

  const routeBrands = buildBrandsArray(
    routeParams.brand,
    filters.brands,
    hasFilterCategories,
    filterSummary,
    brandTaxonomy,
  );
  if (routeBrands && routeBrands.length > 0 && (!filters.brands || filters.brands.length === 0)) {
    params.brands = routeBrands;
  }

  if (routeParams.productTag) {
    params.productTagSlugs = routeParams.productTag;
  }

  addFilterParametersToRequest(params, filters);

  return params;
};
